import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { Paper } from '@material-ui/core';
import { DeleteOutlined, PersonAddAltOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

import { Chip } from 'components/Chip';
import { CommunityEntityType, useCommunityMembersQuery } from 'generated/graphql';
import { TablePaginationActionsV5 } from 'retired/TablePaginationActions';
import UserChip from 'components/UserChip';
import { useDebounce } from 'utils/useDebounce';
import type { CommunityMember } from 'containers/CommunityPage/types';

import { RemoveMembersModal } from './RemoveMembersModal/RemoveMembersModal';
import { AddMembersModal } from './AddMembersModal/AddMembersModal';
import { composeTypeText } from './utils';

type MemberSelection = {
  isExcludingMembers: boolean;
  selection: CommunityMember[];
};

const DELAY_IN_MILLISECONDS = 12000;
const ADD = 'add';
const REMOVE = 'remove';
const composeAlertForSuccessMessage = (type: string) => {
  const keyword = type === ADD ? 'added' : 'removed';
  const action = type === ADD ? 'to' : 'from';
  return (
    <Box marginBottom="8px">
      <Alert severity="success" sx={{ '& .v5-MuiAlert-message': { marginTop: '2px' } }}>
        You have {keyword} user(s) {action} the community, please be patient as it might take a few
        seconds or minutes to fully process. Use the Refresh button to see your changes.
      </Alert>
    </Box>
  );
};

export const MembersList = ({
  communityId,
  communityType,
}: {
  communityId: string;
  communityType: CommunityEntityType | undefined | null;
}): JSX.Element => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [addModalShown, setAddModalShown] = React.useState(false);
  const [onAddSuccessMessage, setShowAddSuccessMessage] = React.useState(false);
  const [onRemoveSuccessMessage, setShowRemoveSuccessMessage] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [removeModalShown, setRemoveModalShown] = React.useState(false);
  const [membersSelection, setMembersSelection] = React.useState<MemberSelection>({
    isExcludingMembers: false,
    selection: [],
  });
  const toggleMember = (member: CommunityMember) =>
    setMembersSelection(({ isExcludingMembers, selection }) => ({
      isExcludingMembers,
      selection: selection.some((m) => m.User.id === member.User.id)
        ? selection.filter((m) => m.User.id !== member.User.id)
        : [...selection, member],
    }));

  const membersQuery = useCommunityMembersQuery({
    variables: {
      communityId,
      skip: page * rowsPerPage,
      take: rowsPerPage,
      search: debouncedSearchTerm || undefined,
    },
    notifyOnNetworkStatusChange: true,
    skip: !communityId,
  });
  const members = membersQuery.data?.community?.members;

  const membersCount =
    (searchTerm ? members?.length : membersQuery.data?.community?.membersCount) || 0;

  const totalMembers = membersQuery.data?.community?.membersCount || 0;
  const colSpan = 4;

  const selectedCount = membersSelection.isExcludingMembers
    ? totalMembers - membersSelection.selection.length
    : membersSelection.selection.length;

  let membersSelectedAlert = null;
  if (selectedCount > 0) {
    if (selectedCount === totalMembers) {
      membersSelectedAlert = <>All members are selected ({selectedCount})</>;
    } else {
      membersSelectedAlert = (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>{selectedCount} members are selected</div>
          <Button
            sx={{ my: -1 }} // so the button doesn't cause the Alert to because too huge
            variant="text"
            onClick={() => setMembersSelection({ isExcludingMembers: true, selection: [] })}
          >
            Select all {totalMembers} members
          </Button>
        </Box>
      );
    }
  }
  return (
    <>
      {onAddSuccessMessage && composeAlertForSuccessMessage(ADD)}
      {onRemoveSuccessMessage && composeAlertForSuccessMessage(REMOVE)}
      <Box
        mb={1}
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          size="small"
          color="primary"
          variant="text"
          onClick={() => {
            setAddModalShown(true);
          }}
        >
          <Box mr={0.5}>Add members</Box>
          <PersonAddAltOutlined />
        </Button>
      </Box>
      <Paper>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            ml: 2,
            mr: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2,
            }}
          >
            <h3 style={{ fontWeight: 500, fontSize: 16 }}>
              {composeTypeText(communityType)} Members
            </h3>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                color="primary"
                onClick={() => {
                  setRemoveModalShown(true);
                }}
                disabled={selectedCount === 0}
              >
                Remove
                <DeleteOutlined />
              </Button>
              <Button
                size="small"
                color="primary"
                onClick={() => {
                  void membersQuery.refetch();
                }}
                disabled={membersQuery.loading}
              >
                Refresh
                <RefreshIcon />
              </Button>
            </Box>
          </Box>
          <TextField
            variant="standard"
            placeholder="Search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm?.length > 0 ? (
                    <IconButton
                      aria-label="Clear input"
                      onClick={(): void => setSearchTerm('')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Alert
          severity="info"
          sx={{
            mx: 2,
            py: 0,
            alignItems: 'center',
            visibility: membersSelectedAlert ? 'inherit' : 'hidden',
          }}
        >
          {membersSelectedAlert}
        </Alert>
        <TableContainer aria-label="list of community members">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 0 }}>
                  <Checkbox
                    color="primary"
                    indeterminate={
                      membersSelection.selection.length < totalMembers &&
                      membersSelection.selection.length > 0
                    }
                    checked={
                      membersSelection.isExcludingMembers
                        ? membersSelection.selection.length < totalMembers
                        : membersSelection.selection.length > 0
                    }
                    onChange={() => {
                      if (selectedCount > 0) {
                        setMembersSelection({ isExcludingMembers: false, selection: [] });
                      } else if (members) {
                        setMembersSelection({
                          isExcludingMembers: false,
                          selection: members,
                        });
                      }
                    }}
                    disabled={false}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Email</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {membersQuery.loading ? (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : membersQuery.error || !members ? (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <Alert severity="error">
                      An error occurred when fetching the community members
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : members?.length === 0 && searchTerm ? (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <Alert severity="info">No matching results.</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member.User.id}>
                    <TableCell style={{ width: 0 }}>
                      <Checkbox
                        color="primary"
                        checked={
                          membersSelection.isExcludingMembers
                            ? !membersSelection.selection.some((m) => m.User.id === member.User.id)
                            : membersSelection.selection.some((m) => m.User.id === member.User.id)
                        }
                        onChange={() => toggleMember(member)}
                      />
                    </TableCell>
                    <TableCell component="th" scope="row" style={{ padding: '4px 0 0 16px' }}>
                      <UserChip user={member.User} />
                    </TableCell>
                    <TableCell style={{ padding: 0 }}>
                      {member.role === 'Member' ? null : (
                        <Chip label={member.role} size="small" style={{ borderRadius: 0 }} />
                      )}
                    </TableCell>
                    <TableCell>{member.User.email}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {Boolean(membersCount) && (
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 25, 50, 100]}
            count={membersCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage): void => setPage(newPage)}
            onRowsPerPageChange={(event): void => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            ActionsComponent={TablePaginationActionsV5}
          />
        )}
      </Paper>
      {removeModalShown && (
        <RemoveMembersModal
          selectedCount={selectedCount}
          totalMembers={totalMembers}
          communityId={communityId}
          members={membersSelection.selection}
          onClickClose={() => {
            setRemoveModalShown(false);
            setShowRemoveSuccessMessage(true);
            setTimeout(() => {
              setShowRemoveSuccessMessage(false);
            }, DELAY_IN_MILLISECONDS);
          }}
          removeMember={(member) => {
            setMembersSelection(({ isExcludingMembers, selection }) => {
              const withoutCurrent = selection.filter((m) => m.User.id !== member.User.id);
              return {
                isExcludingMembers,
                selection: isExcludingMembers ? [...withoutCurrent, member] : withoutCurrent,
              };
            });
          }}
          isExcludingMembers={membersSelection.isExcludingMembers}
        />
      )}
      {addModalShown && (
        <AddMembersModal
          communityId={communityId}
          onClickClose={() => {
            setAddModalShown(false);
            setShowAddSuccessMessage(true);
            setTimeout(() => {
              setShowAddSuccessMessage(false);
            }, DELAY_IN_MILLISECONDS);
          }}
        />
      )}
    </>
  );
};
